import React from 'react';
import { ChevronLeft, ChevronRight, CheckCircle2, ArrowRight } from 'lucide-react';

interface SymptomBlockPagerProps {
  totalItems: number;
  blockSize: number;
  currentBlock: number;
  onBlockChange: (newBlock: number) => void;
  affirmativeCount: number;
  targetCount: number;
  onAdvancePhase: () => void;
  advanceButtonText?: string;
  isSubmitting?: boolean;
}

export const SymptomBlockPager: React.FC<SymptomBlockPagerProps> = ({
  totalItems,
  blockSize,
  currentBlock,
  onBlockChange,
  affirmativeCount,
  targetCount,
  onAdvancePhase,
  advanceButtonText = 'Proceed to next phase',
  isSubmitting = false,
}) => {
  if (totalItems === 0) return null;

  const totalBlocks = Math.ceil(totalItems / blockSize);
  const safeBlock = Math.min(currentBlock, totalBlocks - 1);
  const startItem = safeBlock * blockSize + 1;
  const endItem = Math.min((safeBlock + 1) * blockSize, totalItems);
  const isTargetReached = affirmativeCount >= targetCount;
  const isLastBlock = safeBlock >= totalBlocks - 1;

  let bannerElement = null;
  if (isTargetReached) {
    bannerElement = (
      <div className="alert alert-success shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2">
          <CheckCircle2 size={20} className="text-success-content shrink-0" />
          <span>
            The minimum sufficient symptoms have been answered. You can proceed to the next phase or continue adding symptoms.
          </span>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-primary shrink-0"
          onClick={onAdvancePhase}
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : advanceButtonText}
        </button>
      </div>
    );
  } else if (isLastBlock) {
    bannerElement = (
      <div className="alert alert-info shadow-sm text-sm">
        <span>
          You have reached the end of the questions for this phase. Click on <strong>{advanceButtonText}</strong> to save and proceed.
        </span>
      </div>
    );
  }

  return (
    <div className="bg-base-100 border border-base-200 rounded-xl p-4 shadow-sm space-y-4 my-4">
      {/* Target notification banner */}
      {bannerElement}

      {/* Main control row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {/* Counter badges */}
        <div className="flex items-center gap-2">
          <span className="badge badge-neutral text-xs">
            Block {safeBlock + 1} of {totalBlocks} ({startItem}-{endItem} of {totalItems})
          </span>
        </div>

        {/* Block navigation buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="btn btn-sm btn-outline"
            disabled={safeBlock === 0}
            onClick={() => onBlockChange(safeBlock - 1)}
          >
            <ChevronLeft size={16} /> Previous
          </button>

          <span className="text-xs text-base-content/70 px-1">
            {safeBlock + 1} / {totalBlocks}
          </span>

          <button
            type="button"
            className="btn btn-sm btn-outline"
            disabled={safeBlock >= totalBlocks - 1}
            onClick={() => onBlockChange(safeBlock + 1)}
          >
            Next <ChevronRight size={16} />
          </button>

          <button
            type="button"
            className={`btn btn-sm ${isTargetReached || isLastBlock ? 'btn-success' : 'btn-primary'} ml-2`}
            onClick={onAdvancePhase}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              'Saving...'
            ) : (
              <>
                {advanceButtonText} <ArrowRight size={14} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

